import {
  Alert,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { Suspense } from 'preact/compat'
import { useRef, useState } from 'preact/hooks'
import { lazy } from 'preact-iso'
import type { PositionFields } from '../api/generated/PositionFields'
import { useAction } from '../api/useAction'
import { insertBlock } from './insert'

// Only for an existing position, whose page is loaded on demand: keeps it out of the main bundle
const MediaToolbar = lazy(() => import('./MediaToolbar').then((m) => m.MediaToolbar))

export const emptyPosition: PositionFields = {
  name: '',
  location: '',
  job_description: '',
  helps: false,
}

const inputValue = (e: Event) => (e.target as HTMLInputElement).value

/** Fields of a position; `media` is the existing position whose job description gets media. */
export function PositionForm({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
  media,
}: {
  initial: PositionFields
  submitLabel: string
  onSubmit: (fields: PositionFields) => Promise<void>
  onCancel?: () => void
  media?: { slug: string; positionId: string }
}) {
  const [fields, setFields] = useState(initial)
  const description = useRef<HTMLTextAreaElement>(null)
  const [uploading, setUploading] = useState(false)
  const { error, pending, run } = useAction()
  const submit = (e: Event) => {
    e.preventDefault()
    run(() => onSubmit(fields))
  }
  const insert = (markdown: string) => {
    const input = description.current
    setFields((current) => {
      const { text, caret } = insertBlock(
        current.job_description,
        input?.selectionStart ?? current.job_description.length,
        input?.selectionEnd ?? current.job_description.length,
        markdown,
      )
      requestAnimationFrame(() => {
        input?.focus()
        input?.setSelectionRange(caret, caret)
      })
      return { ...current, job_description: text }
    })
  }
  return (
    <Stack component="form" spacing={2} onSubmit={submit}>
      <TextField
        label="Nom du poste"
        required
        slotProps={{ htmlInput: { maxLength: 200 } }}
        value={fields.name}
        onInput={(e) => setFields({ ...fields, name: inputValue(e) })}
      />
      <TextField
        label="Lieu"
        slotProps={{ htmlInput: { maxLength: 200 } }}
        value={fields.location}
        onInput={(e) => setFields({ ...fields, location: inputValue(e) })}
      />
      <TextField
        label="Fiche de poste"
        multiline
        minRows={8}
        inputRef={description}
        helperText={`Mise en forme : une ligne qui commence par « # » devient un titre, par « - » une puce ; **texte** s'affiche en gras.${media ? '' : ' Images, vidéos et vocaux : une fois le poste créé.'}`}
        slotProps={{ htmlInput: { maxLength: 20000 } }}
        value={fields.job_description}
        onInput={(e) => setFields({ ...fields, job_description: inputValue(e) })}
      />
      <Stack>
        <FormControlLabel
          control={
            <Switch
              checked={fields.helps}
              onChange={(e) =>
                setFields({ ...fields, helps: (e.target as HTMLInputElement).checked })
              }
            />
          }
          label="Poste d'aide"
        />
        <Typography variant="body2" color="text.secondary">
          Ses bénévoles en poste reçoivent les demandes d'aide, avec les organisateurs, et peuvent
          répondre « J'y vais ».
        </Typography>
      </Stack>
      {media && (
        <Suspense fallback={null}>
          <MediaToolbar
            slug={media.slug}
            positionId={media.positionId}
            onInsert={insert}
            onPendingChange={setUploading}
          />
        </Suspense>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction="row" spacing={1}>
        <Button type="submit" variant="contained" loading={pending} disabled={uploading}>
          {submitLabel}
        </Button>
        {onCancel && (
          <Button onClick={onCancel} disabled={uploading}>
            Annuler
          </Button>
        )}
      </Stack>
    </Stack>
  )
}
